import React, { useEffect, useState, useContext } from "react";
import { TENANT_PLATFORM } from '@hiver/bifrost-js';
import EmailProviderOption from "./EmailProviderOption";
import {
    Label,
    StartedWrapper,
    ImageWrapper,
    SecondWrapper,
    Title,
    LogoWrapper,
    LoginButton,
    StyledInput,
    ErrorMessage,
    FlexBox,
    LogoImage,
    EmailLabelText,
    EmailProviderOptionsWrapper,
    EmailProviderWrapper,
    SelectLoginButtonTextWrapper,
    ProductSelectionWrapper,
    ProductOptionsWrapper,
    RadioOption,
    RadioInput,
    StyledTooltip,
} from "./styled.js";
import loginBanner from "../../images/loginBanner.svg";
import RedAlert from "../../images/KB-icon-alert-error.svg";
import {
    LOG_IN,
    CONTINUE,
    EMAIL_PLACEHOLDER,
    PASSWORD_PLACEHOLDER,
    LOG_IN_DESCRIPTION,
    WELCOME,
    DIFF_EMAIL_ERROR,
    NO_ACCESS_ERROR,
    LOGO_IMG,
    selectEmailProviderText,
    selectProductText,
    emailLabel,
    passwordLabel,
    EMAIL_SELECTOR_PROVIDERS,
    EMAIL_PROVIDER_OTHER,
    PRODUCT_OPTIONS,
    PRODUCT_TYPE,
    AUTH_PROVIDER,
    OTP_LABEL,
} from "./constants";
import { validateEmail } from "../../Constants/utility.js";
import { KBContext, AutherContext, ToastContext } from "../../Context";
import { getLogo, initiateLoginHOT } from "./utils.js";
import { getEmailProductsHOT, passwordSignInHOT, validateTokenHOT } from "../../Service/LoginServices";
import useNavigateWithSearch from "../../Context/KBContext/hooks/useNavigateWithSearch.js";

export default function OldLoginPage() {
    const { setShowToast } = useContext(ToastContext);
    const { data : kbData } = useContext(KBContext);

    const kbTenant = (typeof kbData?.tenant === 'string') ? kbData.tenant.toLowerCase() : null;
    const isHigKb = kbTenant === 'gmail';
    const isOmniKb = kbTenant != null && !isHigKb;

    const [emailAddress, setEmailAddress] = useState("");
    const [password, setPassword] = useState("");
    const [selectedEmailProvider, setSelectedEmailProvider] = useState(TENANT_PLATFORM.GMAIL);
    const [selectedProduct, setSelectedProduct] = useState(isHigKb ? PRODUCT_TYPE.HIG : PRODUCT_TYPE.OMNI);
    const [showLoginError, setShowLoginError] = useState(false);
    const [otherStep, setOtherStep] = useState("email");
    const [otherMfaEnabled, setOtherMfaEnabled] = useState(null);
    const [otherLoginLoading, setOtherLoginLoading] = useState(false);
    const [otherLoginError, setOtherLoginError] = useState("");
    const [otp, setOtp] = useState("");
    const {
      setRefetchToken,
      setRedirectPage,
      setEmail,
      showError: showDifferentEmailError,
      hasPermission,
      loginInitiated,
      setLoginInitiated,
      initiateLogin,
      initiateAuthWithUserDetails,
    } = useContext(AutherContext);
    const navigate = useNavigateWithSearch();

    const getAccessTokenFromResponse = (response) => {
        const headers = response?.headers || {};
        const accessToken = headers["access-token"] ?? headers["Access-Token"];
        if (accessToken) return accessToken;
        const auth = headers.Authorization ?? headers.authorization;
        if (auth && /^Bearer\s+/i.test(auth)) return auth.replace(/^Bearer\s+/i, "").trim();
        return null;
    };

    const handleOtherEmailContinue = async () => {
        if (!validateEmail(emailAddress)) {
            setOtherLoginError("Please enter a valid email address.");
            return;
        }
        setOtherLoginError("");
        setOtherLoginLoading(true);
        try {
            const verifyRes = await getEmailProductsHOT(emailAddress);
            const data = verifyRes?.data || {};
            const provider =
                data.provider ??
                data.password_login_provider ??
                data.email_provider;
            if (provider === "forwarding") {
                const mfaEnabled = data.mfa_enabled === true || data.mfaEnabled === true;
                setOtherMfaEnabled(mfaEnabled);
                setOtherStep("password");
            } else if (provider == null) {
                setShowToast("Email is not part of Hiver.");
            } else {
                setShowToast("Please use Gmail or Outlook to sign in with this email.");
            }
        } catch (err) {
            const message = err?.response?.data?.message || err?.response?.data?.errors?.[0] || err?.message || "Something went wrong. Please try again.";
            setShowToast(message);
            setOtherLoginError(message);
        } finally {
            setOtherLoginLoading(false);
        }
    };

    const handleOtherPasswordLogin = async () => {
        setOtherLoginError("");
        setOtherLoginLoading(true);
        try {
            const signInResponse = await passwordSignInHOT(emailAddress, password);
            const accessToken = getAccessTokenFromResponse(signInResponse);
            if (accessToken) {
                const validateRes = await validateTokenHOT(accessToken);
                const payloadData = validateRes?.data?.data;
                if (initiateAuthWithUserDetails && payloadData?.id != null && payloadData?.accounts?.[0]?.id != null) {
                    await initiateAuthWithUserDetails({ user_id: payloadData.id, usergroup_id: payloadData.accounts[0].id });
                }
            }
            navigate("/");
        } catch (err) {
            const message = err?.response?.data?.message || err?.response?.data?.errors?.[0] || err?.message || "Login failed. Please try again.";
            setShowToast(message);
            setOtherLoginError(message);
        } finally {
            setOtherLoginLoading(false);
        }
    };

    const handleOtpLogin = async (otpAttempt) => {
        setOtherLoginError("");
        setOtherLoginLoading(true);
        try {
            const signInResponse = await passwordSignInHOT(emailAddress, password, otpAttempt);
            const accessToken = getAccessTokenFromResponse(signInResponse);
            if (accessToken) {
                const validateRes = await validateTokenHOT(accessToken);
                const payloadData = validateRes?.data?.data;
                if (initiateAuthWithUserDetails && payloadData?.id != null && payloadData?.accounts?.[0]?.id != null) {
                    await initiateAuthWithUserDetails({ user_id: payloadData.id, usergroup_id: payloadData.accounts[0].id });
                }
            }
            navigate("/");
        } catch (err) {
            const message = err?.response?.data?.message || err?.response?.data?.errors?.[0] || err?.message || "Invalid verification code. Please try again.";
            setShowToast(message);
            setOtherLoginError(message);
        } finally {
            setOtherLoginLoading(false);
        }
    };

    const handleOtherPasswordAction = () => {
        if (!password.trim()) {
            setOtherLoginError("Please enter your password.");
            return;
        }
        if (otherMfaEnabled) {
            if (!otp.trim() || otp.length < 6) {
                setOtherLoginError("Please enter the 6-digit verification code.");
                return;
            }
            setOtherLoginError("");
            handleOtpLogin(otp);
        } else {
            setOtherLoginError("");
            handleOtherPasswordLogin();
        }
    };

    const handleOtherLoginButton = () => {
        if (otherStep === "email") return handleOtherEmailContinue();
        if (otherStep === "password") return handleOtherPasswordAction();
    };

    const handleLoginButton = () => {
        if (selectedEmailProvider === EMAIL_PROVIDER_OTHER) {
            return handleOtherLoginButton();
        }
        // If email provider is Gmail and product is HIG, perform legacy login flow with email
        if (selectedEmailProvider === TENANT_PLATFORM.GMAIL && selectedProduct === PRODUCT_TYPE.HIG) {
            if (initiateLogin) {
                return initiateLogin({ email: emailAddress });
            }
            // Fallback for old auth context
            setRefetchToken(true);
            setRedirectPage(true);
            setEmail(emailAddress);
            return;
        }

        // If email provider is Outlook, perform HOT login with provider "microsoft"
        if (selectedEmailProvider === TENANT_PLATFORM.HIVERWEB) {
            return initiateLoginHOT({ provider: AUTH_PROVIDER.MICROSOFT, onError: setShowToast });
        }

        // If email provider is Gmail and product is Omni, perform HOT login with provider "google"
        if (selectedEmailProvider === TENANT_PLATFORM.GMAIL && selectedProduct === PRODUCT_TYPE.OMNI) {
            return initiateLoginHOT({ provider: AUTH_PROVIDER.GOOGLE, onError: setShowToast });
        }
    };

    const handleChangeEmailProvider = (emailProviderName) => {
        if(selectedEmailProvider !== emailProviderName) {
            setSelectedEmailProvider(emailProviderName);
            if (emailProviderName !== EMAIL_PROVIDER_OTHER) {
                setOtherStep("email");
                setOtherMfaEnabled(null);
                setOtherLoginError("");
            }
        }
    };

    const handleProductChange = (e) => {
        setSelectedProduct(e.target.value);
    };

    useEffect(() => {
        setShowLoginError(loginInitiated && (showDifferentEmailError || !hasPermission));
    }, [hasPermission, loginInitiated, showDifferentEmailError]);

    useEffect(() => {
        const { isInternal } = kbData;
        if (isInternal !== undefined) {
            if ((isInternal && hasPermission) || !isInternal) {
                setLoginInitiated(false);
                navigate("/");
            }
        }
    }, [kbData, hasPermission, setLoginInitiated, navigate]);

    const handleOnChange = (e) => {
        e?.stopPropagation();
        const { value } = e.target || {};
        setEmailAddress(value ?? "");
    };

    const isOtherSelected = selectedEmailProvider === EMAIL_PROVIDER_OTHER;
    const otherEmailStep = isOtherSelected && otherStep === "email";
    const otherPasswordStep = isOtherSelected && otherStep === "password";

    const getIsDisabled = () => {
        if (isOtherSelected) {
            if (otherStep === "email") return !validateEmail(emailAddress) || otherLoginLoading;
            if (otherStep === "password") {
                const passwordOk = !!password.trim();
                const otpOk = !otherMfaEnabled || (!!otp.trim() && otp.length >= 6);
                return !passwordOk || !otpOk || otherLoginLoading;
            }
            return false;
        }
        return selectedEmailProvider === TENANT_PLATFORM.GMAIL && selectedProduct === PRODUCT_TYPE.HIG
            ? !validateEmail(emailAddress)
            : false;
    };
    const isDisabled = getIsDisabled();

    const getOtherPrimaryButtonLabel = () => (otherStep === "email" ? CONTINUE : LOG_IN);
    const otherPrimaryButtonLabel = getOtherPrimaryButtonLabel();

    const getPrimaryButtonText = () => {
        if (otherLoginLoading && isOtherSelected) return "Signing in…";
        if (isOtherSelected) return otherPrimaryButtonLabel;
        return LOG_IN;
    };

    const handleKeyPress = (e) => {
        e?.stopPropagation();
        if (e.key === "Enter" && !isDisabled) {
            handleLoginButton();
        }
    };

    let logo = getLogo(kbData);
    logo = logo ? LOGO_IMG.replaceAll('{{LOGO}}', logo) : false;

    return (
        <StartedWrapper>
            <ImageWrapper>
                <img alt='getStartedScreen' src={loginBanner} />
            </ImageWrapper>
            <SecondWrapper>
                {logo && (
                    <LogoWrapper>
                        <LogoImage alt="Logo" src={logo} />
                    </LogoWrapper>
                )}
                <Title>{WELCOME}</Title>
                <Label>{LOG_IN_DESCRIPTION}</Label>
                {!isHigKb && (
                    <EmailProviderWrapper>
                        <SelectLoginButtonTextWrapper>{selectEmailProviderText}</SelectLoginButtonTextWrapper>
                        <EmailProviderOptionsWrapper>
                            {EMAIL_SELECTOR_PROVIDERS.map((provider) => (
                                <EmailProviderOption
                                key={provider.TENANT_ID}
                                provider={provider}
                                isSelected={selectedEmailProvider === provider.TENANT_ID}
                                onSelect={handleChangeEmailProvider}
                                />
                            ))}
                        </EmailProviderOptionsWrapper>
                    </EmailProviderWrapper>
                )}
                {selectedEmailProvider === TENANT_PLATFORM.GMAIL && !isHigKb && !isOmniKb && (
                    <ProductSelectionWrapper>
                        <SelectLoginButtonTextWrapper>{selectProductText}</SelectLoginButtonTextWrapper>
                        <ProductOptionsWrapper>
                            {PRODUCT_OPTIONS.map((product) => (
                                <RadioOption key={product.ID}>
                                    <RadioInput
                                        type="radio"
                                        name="product"
                                        value={product.VALUE}
                                        checked={selectedProduct === product.VALUE}
                                        onChange={handleProductChange}
                                    />
                                    {product.NAME}
                                </RadioOption>
                            ))}
                        </ProductOptionsWrapper>
                    </ProductSelectionWrapper>
                )}
                {otherEmailStep && (
                    <>
                        <EmailLabelText>{emailLabel}</EmailLabelText>
                        <StyledInput
                            type="email"
                            className={otherLoginError ? "error" : ""}
                            placeholder={EMAIL_PLACEHOLDER}
                            value={emailAddress}
                            onChange={handleOnChange}
                            onKeyDown={handleKeyPress}
                        />
                        {otherLoginError && (
                            <FlexBox $otherProvider={isOtherSelected}>
                                <StyledTooltip
                                    $otherProvider={isOtherSelected}
                                    src={RedAlert}
                                    alt=""
                                    width={16}
                                    height={16}
                                    aria-hidden
                                />
                                <ErrorMessage $otherProvider={isOtherSelected}>{otherLoginError}</ErrorMessage>
                            </FlexBox>
                        )}
                    </>
                )}
                {otherPasswordStep && (
                    <>
                        <EmailLabelText>{emailLabel}</EmailLabelText>
                        <StyledInput
                            type="email"
                            value={emailAddress}
                            readOnly
                            style={{ backgroundColor: "#f5f7fa", cursor: "default" }}
                        />
                        <EmailLabelText>{passwordLabel}</EmailLabelText>
                        <StyledInput
                            type="password"
                            placeholder={PASSWORD_PLACEHOLDER}
                            value={password}
                            onChange={(e) => setPassword(e.target.value ?? "")}
                            onKeyDown={(e) => e.key === "Enter" && !isDisabled && handleLoginButton()}
                        />
                        {otherMfaEnabled && (
                            <>
                                <EmailLabelText>{OTP_LABEL}</EmailLabelText>
                                <StyledInput
                                    type="text"
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    placeholder="e.g. 234678"
                                    value={otp}
                                    onChange={(e) => setOtp((e.target.value || "").replaceAll(/\D/g, "").slice(0, 6))}
                                    onKeyDown={(e) => e.key === "Enter" && !isDisabled && handleLoginButton()}
                                    disabled={otherLoginLoading}
                                />
                            </>
                        )}
                        {otherLoginError && (
                            <FlexBox $otherProvider={isOtherSelected}>
                                <StyledTooltip
                                    $otherProvider={isOtherSelected}
                                    src={RedAlert}
                                    alt=""
                                    width={16}
                                    height={16}
                                    aria-hidden
                                />
                                <ErrorMessage $otherProvider={isOtherSelected}>{otherLoginError}</ErrorMessage>
                            </FlexBox>
                        )}
                    </>
                )}
                {selectedProduct === PRODUCT_TYPE.HIG && selectedEmailProvider === TENANT_PLATFORM.GMAIL && (
                    <>
                        <EmailLabelText>{emailLabel}</EmailLabelText>
                        <StyledInput
                            className={showLoginError ? 'error' : ''}
                            onKeyDown={handleKeyPress}
                            placeholder={EMAIL_PLACEHOLDER}
                            value={emailAddress}
                            onChange={handleOnChange}
                        />
                        {showLoginError && (
                            <FlexBox>
                                <ErrorMessage>
                                    {showDifferentEmailError ? DIFF_EMAIL_ERROR : NO_ACCESS_ERROR}
                                </ErrorMessage>
                            </FlexBox>
                        )}
                    </>
                )}
                <LoginButton
                    $otherProvider={isOtherSelected}
                    className={isDisabled ? "disabled" : ""}
                    aria-label={isOtherSelected ? otherPrimaryButtonLabel : LOG_IN}
                    onClick={handleLoginButton}
                >
                    {getPrimaryButtonText()}
                </LoginButton>
            </SecondWrapper>
        </StartedWrapper>
    );
}
